/*
  TemplatePickerDialog — pick a starter program template.

  Lists Clinical Pilates / GLA:D / Onero / PD Warrior templates from
  GET /api/exercise-program-templates, previews the selected one in a
  right-hand pane, and instantiates it as a fresh draft program via
  POST /api/health-record/{email}/exercise-programs/from-template.
*/
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Sparkles, BookOpen, CheckCircle2, AlertTriangle, Target, Wand2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import TemplateManageDialog from "./TemplateManageDialog";

export default function TemplatePickerDialog({ open, onOpenChange, email, onCreated }) {
  const { can } = useAuth();
  const canManage = can("owner", "manager");
  const [templates, setTemplates] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [instantiating, setInstantiating] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    loadTemplates();
  }, [open]);

  function loadTemplates() {
    setLoading(true);
    api.get("/exercise-program-templates")
      .then((r) => {
        setTemplates(r.data?.templates || []);
        if (!selectedCode && r.data?.templates?.length) setSelectedCode(r.data.templates[0].code);
      })
      .catch((e) => toast.error("Couldn't load templates", {
        description: e?.response?.data?.detail || e.message,
      }))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!selectedCode) { setDetail(null); return; }
    api.get(`/exercise-program-templates/${selectedCode}`)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(null));
  }, [selectedCode]);

  async function instantiate() {
    if (!selectedCode || !email) return;
    setInstantiating(true);
    try {
      const enc = encodeURIComponent(email);
      const r = await api.post(
        `/health-record/${enc}/exercise-programs/from-template`,
        { code: selectedCode },
      );
      toast.success(`Created draft: ${r.data.title}`);
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error("Couldn't create program", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setInstantiating(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[94vw] max-h-[85vh] overflow-hidden flex flex-col"
                     data-testid="template-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-700" />
            Start from a template
            {canManage && (
              <Button
                size="sm"
                onClick={() => setManageOpen(true)}
                className="ml-auto h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="template-manage-open"
                title="Edit any template's sections, exercises, precautions and defaults — changes apply to future programs instantiated from it."
              >
                <Wand2 className="h-3.5 w-3.5 mr-1" /> Edit templates
              </Button>
            )}
          </DialogTitle>
          <DialogDescription>
            Evidence-based Australian exercise programs. Instantiating drops
            a fresh <b>draft</b> onto the client — nothing is prescribed
            until you activate it.
            {canManage && (
              <span className="block mt-1 text-xs text-indigo-800">
                Owner/Manager tip: click <b>Edit templates</b> above to
                tweak any template's sections, exercises or precautions —
                new programs pick up your changes immediately.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-sm text-zinc-500">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading templates…
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3 overflow-hidden">
            {/* Template list */}
            <div className="overflow-y-auto space-y-1 pr-1">
              {templates.map((t) => (
                <button
                  key={t.code}
                  onClick={() => setSelectedCode(t.code)}
                  className={cn(
                    "w-full text-left px-2 py-2 rounded border transition-colors",
                    selectedCode === t.code
                      ? "border-indigo-300 bg-indigo-50/50"
                      : "border-transparent hover:bg-zinc-100",
                  )}
                  data-testid={`template-${t.code}`}
                >
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-2xs text-zinc-500 mt-0.5">
                    {t.section_count} sections · {t.exercise_count} exercises
                  </div>
                </button>
              ))}
            </div>

            {/* Preview pane */}
            <div className="overflow-y-auto pr-1">
              {!detail ? (
                <div className="text-sm text-zinc-500 py-10 text-center">
                  <BookOpen className="h-6 w-6 mx-auto mb-2 text-zinc-400" />
                  Pick a template to preview.
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <h3 className="text-lg font-semibold">{detail.name}</h3>
                    <p className="text-sm text-zinc-600 mt-1">{detail.short_description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(detail.tags || []).map((t) => (
                        <Badge key={t} variant="outline" className="text-2xs">{t}</Badge>
                      ))}
                    </div>
                  </div>

                  {detail.goals?.length > 0 && (
                    <section>
                      <div className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1 mb-1">
                        <Target className="h-3 w-3" /> Goals
                      </div>
                      <ul className="text-sm text-zinc-700 list-disc pl-5 space-y-0.5">
                        {detail.goals.map((g, i) => <li key={i}>{g}</li>)}
                      </ul>
                    </section>
                  )}

                  {detail.precautions?.length > 0 && (
                    <section className="rounded border border-amber-200 bg-amber-50 p-2">
                      <div className="text-xs uppercase tracking-wide text-amber-800 flex items-center gap-1 mb-1">
                        <AlertTriangle className="h-3 w-3" /> Precautions
                      </div>
                      <ul className="text-sm text-amber-900 list-disc pl-5 space-y-0.5">
                        {detail.precautions.map((p, i) => <li key={i}>{p}</li>)}
                      </ul>
                    </section>
                  )}

                  <section>
                    <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
                      Sections
                    </div>
                    <div className="space-y-2">
                      {(detail.sections || []).map((s) => (
                        <div key={s.key} className="border border-zinc-200 rounded p-2">
                          <div className="text-sm font-medium">{s.label}</div>
                          {s.narrative && (
                            <p className="text-xs text-zinc-600 mt-0.5">{s.narrative}</p>
                          )}
                          <ul className="text-xs text-zinc-700 mt-1 pl-4 list-disc space-y-0.5">
                            {(s.exercises || []).map((ex) => (
                              <li key={ex.id}>
                                <b>{ex.name}</b>
                                {ex.sets && ` — ${ex.sets}×${ex.reps || ""}`}
                                {ex.hold && ` ${ex.hold}`}
                                {ex.load && ` @ ${ex.load}`}
                                {ex.precautions && (
                                  <span className="text-amber-700 ml-1">
                                    · ⚠ {ex.precautions}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="border-t pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={instantiate}
            disabled={!selectedCode || instantiating}
            data-testid="template-instantiate-btn"
          >
            {instantiating
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Creating draft…</>
              : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Create draft from template</>}
          </Button>
        </DialogFooter>
      </DialogContent>
      <TemplateManageDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        onChanged={loadTemplates}
      />
    </Dialog>
  );
}
